import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  getBytes,
  StorageReference
} from 'firebase/storage';
import { storage } from './init';

/** Per-user cumulative media quota (bytes) and per-file Storage-rule backstop. 50 MB. */
export const UPLOAD_LIMIT = 50000000;

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : 'bin';
}

function uniqueId(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(
    36
  )}`;
}

export interface UploadResult {
  url: string;
  size: number;
  fullPath: string;
  name: string;
}

export async function uploadFile(
  uid: string,
  file: File
): Promise<UploadResult> {
  const fullPath = `assets/${uid}/${uniqueId()}.${extOf(file.name)}`;
  const objectRef = ref(storage, fullPath);
  const snap = await uploadBytes(objectRef, file);
  const url = await getDownloadURL(objectRef);
  return {
    url,
    size: snap.metadata.size ?? file.size,
    fullPath,
    name: file.name
  };
}

export async function deleteFile(fullPath: string): Promise<void> {
  await deleteObject(ref(storage, fullPath));
}

export interface AssetItem {
  key: string;
  name: string;
  url: string;
  size: number;
}

export async function listFiles(
  uid: string
): Promise<{ assets: AssetItem[]; totalSize: number }> {
  const dirRef = ref(storage, `assets/${uid}`);
  const res = await listAll(dirRef);
  // Resolve each item independently: one object deleted out-of-band (404) or one transient
  // metadata/download-URL failure must NOT fail the whole listing. Drop the failures; render the rest.
  const settled = await Promise.allSettled(
    res.items.map(async (itemRef: StorageReference) => {
      const [meta, url] = await Promise.all([
        getMetadata(itemRef),
        getDownloadURL(itemRef)
      ]);
      return {
        key: itemRef.fullPath,
        // Fallback name only; getAssets() derives the real display name from the sketch file that
        // references this object (upstream logic — a sketch file is the source of truth for the name).
        name: meta.name,
        url,
        size: meta.size ?? 0
      };
    })
  );
  const assets: AssetItem[] = [];
  settled.forEach((r) => {
    if (r.status === 'fulfilled') assets.push(r.value);
  });
  const totalSize = assets.reduce((sum, a) => sum + a.size, 0);
  return { assets, totalSize };
}

/**
 * Copy an object to a new path under `uid` (for sketch clone). `source` is a gs:// path or a Firebase
 * download URL. Note: this buffers the whole object into memory (getBytes → uploadBytes), so peak heap
 * is ~2× the file size — fine under the 50 MB per-file cap, but not a streaming copy.
 */
export async function copyFile(
  uid: string,
  source: string
): Promise<UploadResult> {
  const sourceRef = ref(storage, source);
  const bytes = await getBytes(sourceRef);
  const { name } = sourceRef;
  const fullPath = `assets/${uid}/${uniqueId()}.${extOf(name)}`;
  const destRef = ref(storage, fullPath);
  await uploadBytes(destRef, bytes);
  const newUrl = await getDownloadURL(destRef);
  return { url: newUrl, size: bytes.byteLength, fullPath, name };
}
